import dayjs from '../../utils/dayjs';
import {
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  DataTable,
  EmptyState,
  Icon,
  InlineGrid,
  InlineStack,
  Layout,
  Link,
  Page,
  Text,
} from '@shopify/polaris';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SkeletonLoading from 'components/skeletonPageLoading';
import emptyIMG from 'media/images/empty.png';
import {
  ChargeTransaction,
  ChargeTransactionAnalysis,
  helper_fmtNumber,
  useDownloadChargeTransaction,
  useGetChargeTransactionTodayAnalysis,
  useGetChargeTransactions,
  useStopTransaction,
} from 'queries/charge_station.query';

import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChartLineIcon, ClockIcon, OrderIcon, PageDownIcon, StopCircleIcon } from '@shopify/polaris-icons';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import ChargeTransactionFilter from './filter';
import helpers from 'helpers/index';
import Pagination from 'components/pagination';

export default function ChargeTransactionList() {
  const rootSearchPath = window.location.search;
  /**
   * If user apply filter, it will add to URL, then parse URL back to initial state
   */
  let StringQuery: any = helpers.ExtractUrl(rootSearchPath) || false;
  const initialQuery = {
    query: '',
    page: 1,
    limit: 50,
    sort: 'createdAt:desc',
  };
  const [mainQuery, setMainQuery] = useState<any>({
    ...initialQuery,
    ...StringQuery,
  });
  const [debouncedQuery, setDebouncedQuery] = useState<any>(mainQuery);
  const { data: chargeTransactionData, isLoading, isSuccess } = useGetChargeTransactions(debouncedQuery);
  const { data: todayAnalysis } = useGetChargeTransactionTodayAnalysis();

  const [entities, setEntities] = useState<ChargeTransaction[]>([]);
  const [analysis, setAnalysis] = useState<ChargeTransactionAnalysis>({
    totalEnergyKwh: 0,
    totalDurationMinutes: 0,
  });
  const [totalItems, setTotalItems] = useState(0);
  useEffect(() => {
    if (chargeTransactionData) {
      let { entities, analysis, totalItems } = chargeTransactionData;
      setEntities(entities);
      setAnalysis(analysis);
      setTotalItems(totalItems);
    }
  }, [chargeTransactionData]);

  const renderStatusBadge = (status?: ChargeTransaction['status']) => {
    switch (status) {
      case 'STARTED':
        return <Badge tone="warning">Đang sạc</Badge>;
      case 'STOPPED':
        return <Badge tone="success">Hoàn tất</Badge>;
      default:
        return (
          <Text as="span" tone="subdued">
            -
          </Text>
        );
    }
  };

  const formatDuration = (seconds?: number) => {
    if (typeof seconds !== 'number') {
      return '-';
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return [hours, minutes, secs].map((value) => String(value).padStart(2, '0')).join(':');
  };

  const history = useNavigate();
  const { showDeleteConfirm } = useDeleteConfirm();
  const { mutateAsync: stopTransaction } = useStopTransaction();

  const confirmCallback = useCallback(async (cpid, connectorId) => {
    const confirmed = await showDeleteConfirm(
      'Dừng phiên sạc này?',
      `Phiên sạc sẽ dừng lại sau vài giây. Để bắt đầu lại, bạn phải rút sạc ra và bắt đầu phiên mới.`
    );
    if (confirmed) {
      try {
        await stopTransaction({
          cpid,
          connectorId,
        });
      } catch (e) {}
    }
  }, []);

  /**
   * Change page number
   * Must be mainQuery or it will reset mainQuery. BUG!
   */
  const onChangePagination = useCallback((numPage: number, limit: number) => {
    setMainQuery((prevMainQuery) => ({ ...prevMainQuery, page: numPage, limit: limit }));
  }, []);

  useEffect(() => {
    let buildURLSearch = helpers.buildEndUrl(mainQuery);
    if (window.location.search !== buildURLSearch) {
      window.history.replaceState(null, 'charge_transaction', '/charge_transaction' + buildURLSearch);
    }

    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(mainQuery);
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [mainQuery]);

  /**
   * I do not know ...
   * Bug: In React, do NOT remove this code
   */
  const filterCallback = useCallback((_value: any) => {
    if (_value === false) {
      return setMainQuery(initialQuery);
    } else {
      setMainQuery((oldValue) => {
        return { ...oldValue, ..._value };
      });
    }
  }, []);

  const totalInTable = useMemo(
    () =>
      entities.reduce(
        (total, transaction) => ({
          recordCount: total.recordCount + 1,
          energyKwh: total.energyKwh + (Number(transaction.metrics?.energyKwh) || 0),
          durationSeconds: total.durationSeconds + (Number(transaction.metrics?.durationSeconds) || 0),
        }),
        { recordCount: 0, energyKwh: 0, durationSeconds: 0 }
      ),
    [entities]
  );

  const rows = entities?.map((transaction) => {
    const vehicleLabel = transaction.vehicle?.licensePlate ?? transaction.vehicle?.tagId ?? '-';
    const currentSoc = transaction.metrics?.socEnd ?? transaction.metrics?.socStart;
    const socStart = transaction.metrics?.socStart;
    return [
      <Link
        onClick={() => {
          history(`/charge_transaction/view/${transaction.id}`);
        }}
      >
        {transaction.ocppTransactionId ?? '-'}
      </Link>,
      renderStatusBadge(transaction.status),
      dayjs(new Date(transaction.startedAt)).format('HH:mm DD-MM-YYYY'),
      <Link
        onClick={() => {
          history(`/vehicle_management/view/${transaction.vehicleId}`);
        }}
      >
        {vehicleLabel}
      </Link>,
      <Link
        onClick={() => {
          history(`/charge_station/view/${transaction.chargeStationId}`);
        }}
      >
        {transaction.charge_station?.name ?? '-'}
      </Link>,

      typeof socStart === 'number' ? `${socStart}%` : '-',
      typeof currentSoc === 'number' ? `${currentSoc}%` : '-',

      helper_fmtNumber(transaction.metrics.energyKwh),

      formatDuration(transaction.metrics?.durationSeconds),

      transaction.status === 'STARTED' ? (
        <Button
          size="micro"
          icon={StopCircleIcon}
          tone="critical"
          onClick={() => confirmCallback(transaction.charge_station.cpid, transaction.connectorId)}
        >
          Dừng
        </Button>
      ) : null,
    ];
  });

  /** Gán một giao dịch nào đó vào vé nào đó */
  const currentDriverForDownload = useRef<any>(null);
  const { mutateAsync: downloadExcelThisFilter, isPending: downloadGenerating } = useDownloadChargeTransaction();
  const generateDownloadLink = useCallback(async () => {
    try {
      const blob = await downloadExcelThisFilter(debouncedQuery);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `driver_revenue-${currentDriverForDownload.current}.xlsx`;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (e) {}
  }, [debouncedQuery, currentDriverForDownload.current]);

  const emptyState = (
    <EmptyState heading="Chưa có phiên sạc" image={emptyIMG}>
      <p>Không có dữ liệu phiên sạc để hiển thị.</p>
    </EmptyState>
  );

  if (isLoading) {
    return <SkeletonLoading />;
  }

  return (
    <>
      <Helmet>
        <title>Quản lý phiên sạc</title>
      </Helmet>
      <Page
        title="Phiên sạc"
        primaryAction={{
          content: 'Tải về bản này',
          icon: PageDownIcon,
          onAction: () => generateDownloadLink(),
        }}
      >
        <BlockStack gap="400">
          <Text as="p" tone="success">
            Hôm nay
          </Text>
          <InlineGrid gap="400" columns={{ xs: 1, sm: 1, md: 3 }}>
            <Box>
              <Card padding="300">
                <BlockStack gap="100">
                  <InlineStack gap="200" blockAlign="center" wrap={false}>
                    <Icon source={ChartLineIcon} tone="subdued" />
                    <Text as="span" tone="subdued">
                      Tổng điện hôm nay
                    </Text>
                  </InlineStack>
                  <Text variant="headingLg" as="p">
                    {helper_fmtNumber(Math.floor(todayAnalysis?.totalEnergyKwh ?? 0))} kWh
                  </Text>
                </BlockStack>
              </Card>
            </Box>

            <Box>
              <Card padding="300">
                <BlockStack gap="100">
                  <InlineStack gap="200" blockAlign="center" wrap={false}>
                    <Icon source={OrderIcon} tone="subdued" />
                    <Text as="span" tone="subdued">
                      Tổng phiên hôm nay
                    </Text>
                  </InlineStack>
                  <Text variant="headingLg" as="p">
                    {helpers.formatNumber(todayAnalysis?.totalTransactions ?? 0)} phiên
                  </Text>
                </BlockStack>
              </Card>
            </Box>
            <Box>
              <Card padding="300">
                <BlockStack gap="100">
                  <InlineStack gap="200" blockAlign="center" wrap={false}>
                    <Icon source={ClockIcon} tone="subdued" />
                    <Text as="span" tone="subdued">
                      Tổng thời gian hôm nay
                    </Text>
                  </InlineStack>
                  <Text variant="headingLg" as="p">
                    {helpers.formatNumber(Math.floor(todayAnalysis?.totalDurationMinutes ?? 0))} phút
                  </Text>
                </BlockStack>
              </Card>
            </Box>
          </InlineGrid>

          <ChargeTransactionFilter onCallback={filterCallback} loading={isLoading} />

          <Layout>
            <Layout.Section>
              <Card padding="0">
                {isSuccess ? (
                  <>
                    <DataTable
                      hoverable
                      columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
                      headings={[
                        'ID',
                        'Tình trạng',
                        'Bắt đầu',
                        'ID xe/biển số',
                        'Tại trạm',
                        'SoC bắt đầu',
                        'SoC hiện tại',
                        'Kw đã sạc',
                        'Thời gian sạc',
                        '--',
                      ]}
                      rows={rows}
                      totals={[
                        'Tổng',
                        `${totalInTable.recordCount} phiên`,
                        '',
                        '',
                        '',
                        '',
                        '',
                        `${helper_fmtNumber(totalInTable.energyKwh)} kWh`,
                        formatDuration(totalInTable.durationSeconds),
                        '',
                      ]}
                      totalsName={{ singular: 'Tổng', plural: 'Tổng' }}
                      showTotalsInFooter
                      hideScrollIndicator
                    />

                    <br />
                    <br />

                    {totalItems > 0 ? (
                      <Pagination
                        TotalRecord={totalItems}
                        onChange={onChangePagination}
                        pageSize={Number(mainQuery?.limit)}
                        activeCurrentPage={Number(mainQuery?.page)}
                      />
                    ) : null}
                  </>
                ) : (
                  emptyState
                )}
              </Card>

              <br />
              <br />

              <InlineStack gap="400" wrap>
                <Box minWidth="200px">
                  <BlockStack gap="100">
                    <Text as="span" tone="subdued">
                      Tổng điện năng sạc
                    </Text>
                    <Text variant="headingLg" as="p">
                      {helper_fmtNumber(analysis.totalEnergyKwh)} kWh
                    </Text>
                  </BlockStack>
                </Box>
                <Box minWidth="200px">
                  <BlockStack gap="100">
                    <Text as="span" tone="subdued">
                      Tổng phiên sạc
                    </Text>
                    <Text variant="headingLg" as="p">
                      {helpers.formatNumber(totalItems)} phiên
                    </Text>
                  </BlockStack>
                </Box>
                <Box minWidth="200px">
                  <BlockStack gap="100">
                    <Text as="span" tone="subdued">
                      Tổng thời gian sạc
                    </Text>
                    <Text variant="headingLg" as="p">
                      {helpers.formatNumber(Math.floor(analysis.totalDurationMinutes))} phút
                    </Text>
                  </BlockStack>
                </Box>
              </InlineStack>
            </Layout.Section>
          </Layout>
        </BlockStack>
      </Page>
      <br />
      <br />
      <br />
    </>
  );
}
